import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { 
  Car, Plus, Edit, Eye, ShieldCheck, Pause, Play, 
  Archive, Trash2, Calendar, MoreVertical, AlertTriangle, Share2
} from 'lucide-react';
import { getVehiclePhotoUrl, filterValidPhotos, handleVehicleImageError } from '../lib/imageUtils';
import { toast } from 'sonner';
import { HostAppShell } from '../components/HostAppShell';

const VehicleManagement = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [expandedActions, setExpandedActions] = useState(null);
  // P0 SHARE LISTING: Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLinks, setShareLinks] = useState(null);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await api.get('/vehicles/my-vehicles');
      setVehicles(response.data.vehicles || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async (vehicleId) => {
    setActionLoading(vehicleId);
    try {
      await api.post(`/vehicles/${vehicleId}/pause`);
      toast.success('Vehicle paused - hidden from marketplace');
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pause vehicle');
    } finally {
      setActionLoading(null);
      setExpandedActions(null);
    }
  };

  const handleActivate = async (vehicleId) => {
    setActionLoading(vehicleId);
    try {
      await api.post(`/vehicles/${vehicleId}/activate`);
      toast.success('Vehicle activated - now visible in marketplace');
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate vehicle');
    } finally {
      setActionLoading(null);
      setExpandedActions(null);
    }
  };

  const handleUnlist = async (vehicleId) => {
    setActionLoading(vehicleId);
    try {
      await api.post(`/vehicles/${vehicleId}/unlist`);
      toast.success('Vehicle unlisted');
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlist vehicle');
    } finally {
      setActionLoading(null);
      setExpandedActions(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedVehicle) return;
    setActionLoading(selectedVehicle.vehicle_id);
    try {
      await api.delete(`/vehicles/${selectedVehicle.vehicle_id}`);
      toast.success('Vehicle deleted');
      setShowDeleteModal(false);
      setSelectedVehicle(null);
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete vehicle');
    } finally {
      setActionLoading(null);
    }
  };

  const openDeleteModal = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDeleteModal(true);
    setExpandedActions(null);
  };

  // P0 SHARE LISTING: Open share modal and fetch share links
  const openShareModal = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setExpandedActions(null);
    try {
      const response = await api.get(`/vehicles/${vehicle.vehicle_id}/share-links`);
      setShareLinks(response.data);
      setShowShareModal(true);
    } catch (error) {
      toast.error('Failed to load share links');
    }
  };

  // P0 SHARE LISTING: Copy link to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { className: 'bg-green-100 text-green-800', label: 'Active' },
      paused: { className: 'bg-yellow-100 text-yellow-800', label: 'Paused' },
      unlisted: { className: 'bg-orange-100 text-orange-800', label: 'Unlisted' },
      archived: { className: 'bg-slate-100 text-slate-600', label: 'Archived' },
      pending: { className: 'bg-blue-100 text-blue-800', label: 'Pending' },
      inactive: { className: 'bg-red-100 text-red-800', label: 'Inactive' }
    };
    return badges[status] || badges.inactive;
  };

  return (
    <HostAppShell title="My Vehicles" activeId="vehicles" showAddVehicle>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Trust Badge Banner */}
        <Link to="/host/protection" className="block">
          <div className="flex items-center gap-2 text-sm text-slate-600 bg-gradient-to-r from-blue-50 to-transparent p-3 rounded-lg border border-blue-100 hover:border-blue-200 transition-colors" data-testid="protection-trust-badge">
            <ShieldCheck className="w-4 h-4 text-[#004B8D]" />
            <span>Your vehicles are protected with <strong className="text-[#004B8D]">HireHub Protection</strong> — evidence-backed inspections and admin-reviewed disputes</span>
          </div>
        </Link>
        
        {loading ? (
          <div className="flex items-center justify-center py-16 sm:py-20">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-[#004B8D]"></div>
          </div>
        ) : vehicles.length === 0 ? (
          <Card className="border-slate-100">
            <CardContent className="py-12 sm:py-16 text-center">
              <Car className="w-14 h-14 sm:w-16 sm:h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-2">No vehicles yet</h3>
              <p className="text-slate-500 mb-6 text-sm sm:text-base">Start earning by listing your first vehicle</p>
              <Link to="/host/vehicles/new">
                <Button className="bg-[#004B8D] hover:bg-[#003865] h-10 sm:h-11">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Vehicle
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {vehicles.map((vehicle) => {
              const statusBadge = getStatusBadge(vehicle.status);
              const isExpanded = expandedActions === vehicle.vehicle_id;
              
              return (
                <Card key={vehicle.vehicle_id} className="border-slate-100 overflow-hidden" data-testid={`my-vehicle-card-${vehicle.vehicle_id}`}>
                  <div className="aspect-video bg-slate-200 relative">
                    {filterValidPhotos(vehicle.photos)?.[0] ? (
                      <img 
                        src={getVehiclePhotoUrl(filterValidPhotos(vehicle.photos)[0])}
                        alt={`${vehicle.make} ${vehicle.model}`}
                        className="w-full h-full object-cover"
                        onError={(e) => handleVehicleImageError(e, vehicle.vehicle_id)}
                      />
                    ) : null}
                    <div className={`w-full h-full flex items-center justify-center absolute inset-0 ${filterValidPhotos(vehicle.photos)?.[0] ? 'hidden' : ''}`}>
                      <Car className="w-14 h-14 sm:w-16 sm:h-16 text-slate-400" />
                    </div>
                    <Badge className={`absolute top-2 right-2 sm:top-3 sm:right-3 text-xs ${statusBadge.className}`} data-testid={`status-badge-${vehicle.vehicle_id}`}>
                      {statusBadge.label}
                    </Badge>
                    {vehicle.instant_book && (
                      <Badge className="absolute top-2 left-2 sm:top-3 sm:left-3 text-xs bg-[#004B8D] text-white">
                        Instant Book
                      </Badge>
                    )}
                  </div>
                  <CardContent className="p-4 sm:p-5">
                    <h3 className="font-heading font-semibold text-base sm:text-lg mb-1 truncate">
                      {vehicle.make} {vehicle.model}
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-500 mb-2 sm:mb-3 truncate">{vehicle.year} • {vehicle.location}</p>
                    <p className="text-lg sm:text-xl font-bold text-[#004B8D] mb-3 sm:mb-4">${vehicle.daily_rate}/day</p>
                    
                    {/* Primary Actions */}
                    <div className="flex gap-2 mb-2">
                      <Link to={`/host/vehicles/${vehicle.vehicle_id}/edit`} className="flex-1">
                        <Button variant="outline" className="w-full gap-2 h-9 sm:h-10 text-sm" size="sm" data-testid={`edit-vehicle-${vehicle.vehicle_id}`}>
                          <Edit className="w-4 h-4" />
                          Edit
                        </Button>
                      </Link>
                      <Link to={`/vehicles/${vehicle.vehicle_id}`}>
                        <Button variant="outline" size="sm" className="h-9 sm:h-10 w-9 sm:w-10 p-0" data-testid={`view-listing-${vehicle.vehicle_id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-9 sm:h-10 w-9 sm:w-10 p-0"
                        onClick={() => setExpandedActions(isExpanded ? null : vehicle.vehicle_id)}
                        data-testid={`more-actions-${vehicle.vehicle_id}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Expanded Actions */}
                    {isExpanded && (
                      <div className="space-y-2 pt-2 border-t border-slate-100 animate-in slide-in-from-top-2 duration-200">
                        {/* P0 SHARE LISTING: Share button */}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full justify-start gap-2 h-9 text-sm text-blue-700 border-blue-200 hover:bg-blue-50"
                          onClick={() => openShareModal(vehicle)}
                          data-testid={`share-vehicle-${vehicle.vehicle_id}`}
                        >
                          <Share2 className="w-4 h-4" />
                          Share Listing
                        </Button>
                        
                        {/* Availability Calendar */}
                        <Link to={`/host/vehicles/${vehicle.vehicle_id}/edit#availability`} className="block">
                          <Button variant="outline" size="sm" className="w-full justify-start gap-2 h-9 text-sm">
                            <Calendar className="w-4 h-4" />
                            Availability Calendar
                          </Button>
                        </Link>
                        
                        {/* Status Actions */}
                        {vehicle.status === 'active' ? (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full justify-start gap-2 h-9 text-sm text-yellow-700 border-yellow-200 hover:bg-yellow-50"
                            onClick={() => handlePause(vehicle.vehicle_id)}
                            disabled={actionLoading === vehicle.vehicle_id}
                            data-testid={`pause-vehicle-${vehicle.vehicle_id}`}
                          >
                            <Pause className="w-4 h-4" />
                            Pause Listing
                          </Button>
                        ) : vehicle.status === 'paused' || vehicle.status === 'unlisted' ? (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full justify-start gap-2 h-9 text-sm text-green-700 border-green-200 hover:bg-green-50"
                            onClick={() => handleActivate(vehicle.vehicle_id)}
                            disabled={actionLoading === vehicle.vehicle_id}
                            data-testid={`activate-vehicle-${vehicle.vehicle_id}`}
                          >
                            <Play className="w-4 h-4" />
                            Activate Listing
                          </Button>
                        ) : null}
                        
                        {vehicle.status !== 'unlisted' && vehicle.status !== 'archived' && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full justify-start gap-2 h-9 text-sm text-orange-700 border-orange-200 hover:bg-orange-50"
                            onClick={() => handleUnlist(vehicle.vehicle_id)}
                            disabled={actionLoading === vehicle.vehicle_id}
                            data-testid={`unlist-vehicle-${vehicle.vehicle_id}`}
                          >
                            <Archive className="w-4 h-4" />
                            Unlist Vehicle
                          </Button>
                        )}
                        
                        {/* Danger Zone */}
                        <div className="pt-2 border-t border-slate-100">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full justify-start gap-2 h-9 text-sm text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => openDeleteModal(vehicle)}
                            disabled={actionLoading === vehicle.vehicle_id}
                            data-testid={`delete-vehicle-${vehicle.vehicle_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Vehicle
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Vehicle
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone easily.
            </DialogDescription>
          </DialogHeader>
          {selectedVehicle && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-4">
                <p className="font-medium text-slate-900">
                  {selectedVehicle.make} {selectedVehicle.model} ({selectedVehicle.year})
                </p>
                <p className="text-sm text-slate-500 mt-1">{selectedVehicle.location}</p>
              </div>
              <p className="text-sm text-slate-600">
                Deleting will remove this vehicle from your listings. This is only allowed if there are:
              </p>
              <ul className="text-sm text-slate-600 mt-2 space-y-1 list-disc list-inside">
                <li>No active or upcoming bookings</li>
                <li>No pending claims or disputes</li>
              </ul>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={actionLoading}
              data-testid="confirm-delete-vehicle"
            >
              {actionLoading ? 'Deleting...' : 'Delete Vehicle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P0 SHARE LISTING: Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Share2 className="w-5 h-5" />
              Share Listing
            </DialogTitle>
            <DialogDescription>
              Promote your vehicle on social media
            </DialogDescription>
          </DialogHeader>
          {selectedVehicle && shareLinks && (
            <div className="py-4 space-y-4">
              {/* Vehicle Info */}
              <div className="bg-slate-50 border border-slate-100 rounded-lg p-4">
                <p className="font-medium text-slate-900">
                  {selectedVehicle.make} {selectedVehicle.model} ({selectedVehicle.year})
                </p>
                <p className="text-sm text-slate-500 mt-1">${selectedVehicle.daily_rate}/day</p>
              </div>
              
              {/* Copy Link */}
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={shareLinks.listing_url} 
                  readOnly 
                  className="flex-1 px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-sm truncate"
                />
                <Button 
                  variant="outline"
                  onClick={() => copyToClipboard(shareLinks.listing_url)}
                  data-testid="copy-listing-link"
                >
                  Copy
                </Button>
              </div>
              
              {/* Share Buttons */}
              <div className="grid grid-cols-2 gap-2">
                <a 
                  href={shareLinks.share_options.whatsapp} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button variant="outline" className="w-full justify-start gap-2 h-10 text-green-600 border-green-200 hover:bg-green-50" data-testid="share-whatsapp">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                    WhatsApp
                  </Button>
                </a>
                <a 
                  href={shareLinks.share_options.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button variant="outline" className="w-full justify-start gap-2 h-10 text-blue-600 border-blue-200 hover:bg-blue-50" data-testid="share-facebook">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    Facebook
                  </Button>
                </a>
                <a 
                  href={shareLinks.share_options.messenger} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button variant="outline" className="w-full justify-start gap-2 h-10 text-purple-600 border-purple-200 hover:bg-purple-50" data-testid="share-messenger">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.654V24l4.088-2.242c1.092.3 2.246.464 3.443.464 6.627 0 12-4.975 12-11.111C24 4.974 18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.131 3.259L19.752 8l-6.561 6.963z"/></svg>
                    Messenger
                  </Button>
                </a>
                <a 
                  href={shareLinks.share_options.email} 
                  className="block"
                >
                  <Button variant="outline" className="w-full justify-start gap-2 h-10 text-slate-600 border-slate-200 hover:bg-slate-50" data-testid="share-email">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                    Email
                  </Button>
                </a>
                <a 
                  href={shareLinks.share_options.sms} 
                  className="block col-span-2"
                >
                  <Button variant="outline" className="w-full justify-start gap-2 h-10 text-teal-600 border-teal-200 hover:bg-teal-50" data-testid="share-sms">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    SMS
                  </Button>
                </a>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </HostAppShell>
  );
};

export default VehicleManagement;
